import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import Index from "./pages/Index";
const AuthPage = lazy(() => import("./pages/AuthPage"));
const GalleryAdminPage = lazy(() => import("./pages/GalleryAdminPage"));
import NotFound from "./pages/NotFound";
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const CaseStudiesHubPage = lazy(() => import("./pages/CaseStudiesHubPage"));
const CaseStudyDetailPage = lazy(() => import("./pages/CaseStudyDetailPage"));
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
const DiagnosisPage = lazy(() => import("./pages/DiagnosisPage"));
const DiagnosticoSeoPage = lazy(() => import("./pages/DiagnosticoSeoPage"));
const SEOToolsPage = lazy(() => import("./pages/SEOToolsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
// Prototipo visual privado (noindex, fuera del sitemap, sin enlaces públicos)
const RocV2Page = lazy(() => import("./pages/visual-lab/RocV2Page"));

// Legal Pages
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CookiesPolicyPage = lazy(() => import("./pages/CookiesPolicyPage"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const OfficialInformationPage = lazy(() => import("./pages/OfficialInformationPage"));

// Service Pages
const GoogleAdsPage = lazy(() => import("./pages/services/GoogleAdsPage"));
const LocalSEOHubPage = lazy(() => import("./pages/LocalSEOHubPage"));
const LocalSEOHoustonPage = lazy(() => import("./pages/LocalSEOHoustonPage"));
const SEOTheWoodlandsPage = lazy(() => import("./pages/SEOTheWoodlandsPage"));
const GoogleAdsTheWoodlandsPage = lazy(() => import("./pages/GoogleAdsTheWoodlandsPage"));
const BrandingWebTheWoodlandsPage = lazy(() => import("./pages/BrandingWebTheWoodlandsPage"));
const EcommerceMarketplacesTheWoodlandsPage = lazy(() => import("./pages/EcommerceMarketplacesTheWoodlandsPage"));
const SocialMediaTheWoodlandsPage = lazy(() => import("./pages/SocialMediaTheWoodlandsPage"));
const LocationsHubPage = lazy(() => import("./pages/LocationsHubPage"));

// Location Pages
const SEOHoustonPage = lazy(() => import("./pages/locations/SEOHoustonPage"));
const GoogleAdsHoustonPage = lazy(() => import("./pages/locations/GoogleAdsHoustonPage"));
const SEOSpringPage = lazy(() => import("./pages/locations/SEOSpringPage"));
const GoogleAdsSpringPage = lazy(() => import("./pages/locations/GoogleAdsSpringPage"));
const SEOConroePage = lazy(() => import("./pages/locations/SEOConroePage"));
const GoogleAdsConroePage = lazy(() => import("./pages/locations/GoogleAdsConroePage"));
const SEOKatyPage = lazy(() => import("./pages/locations/SEOKatyPage"));
const GoogleAdsKatyPage = lazy(() => import("./pages/locations/GoogleAdsKatyPage"));
const SEOSugarLandPage = lazy(() => import("./pages/locations/SEOSugarLandPage"));
const GoogleAdsSugarLandPage = lazy(() => import("./pages/locations/GoogleAdsSugarLandPage"));
const SEOTomballPage = lazy(() => import("./pages/locations/SEOTomballPage"));
const GoogleAdsTomballPage = lazy(() => import("./pages/locations/GoogleAdsTomballPage"));
const SEOMagnoliaPage = lazy(() => import("./pages/locations/SEOMagnoliaPage"));
const GoogleAdsMagnoliaPage = lazy(() => import("./pages/locations/GoogleAdsMagnoliaPage"));
const SEOMemorialPage = lazy(() => import("./pages/locations/SEOMemorialPage"));
const GoogleAdsMemorialPage = lazy(() => import("./pages/locations/GoogleAdsMemorialPage"));

// Florida Location Pages
const LocationsFloridaHubPage = lazy(() => import("./pages/LocationsFloridaHubPage"));
const SEOOrlandoPage = lazy(() => import("./pages/locations/SEOOrlandoPage"));
const GoogleAdsOrlandoPage = lazy(() => import("./pages/locations/GoogleAdsOrlandoPage"));
const SEOMiamiPage = lazy(() => import("./pages/locations/SEOMiamiPage"));
const GoogleAdsMiamiPage = lazy(() => import("./pages/locations/GoogleAdsMiamiPage"));

// Tool Pages
const WordCounterPage = lazy(() => import("./pages/tools/WordCounterPage"));
const SitemapGeneratorPage = lazy(() => import("./pages/tools/SitemapGeneratorPage"));
const SpellCheckerPage = lazy(() => import("./pages/tools/SpellCheckerPage"));
const BlacklistCheckerPage = lazy(() => import("./pages/tools/BlacklistCheckerPage"));
const SEOMegaToolPage = lazy(() => import("./pages/tools/SEOMegaToolPage"));
const MetaTagsGeneratorPage = lazy(() => import("./pages/tools/MetaTagsGeneratorPage"));
const URLAnalyzerPage = lazy(() => import("./pages/tools/URLAnalyzerPage"));
const KeywordDensityPage = lazy(() => import("./pages/tools/KeywordDensityPage"));
const AIContentGeneratorPage = lazy(() => import("./pages/tools/AIContentGeneratorPage"));
const LocalSEOGMBAnalyzerPage = lazy(() => import("./pages/tools/LocalSEOGMBAnalyzerPage"));
const GooglePenalizationAnalyzerPage = lazy(() => import("./pages/tools/GooglePenalizationAnalyzerPage"));
const CustomChatbotPage = lazy(() => import("./pages/tools/CustomChatbotPage"));
const AIImageGeneratorPage = lazy(() => import("./pages/tools/AIImageGeneratorPage"));
const CodeAssistantPage = lazy(() => import("./pages/tools/CodeAssistantPage"));
const SentimentAnalyzerPage = lazy(() => import("./pages/tools/SentimentAnalyzerPage"));

// Blog Pages
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ImplementarIAMarketing = lazy(() => import("./pages/blog/ImplementarIAMarketing"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const TransitionRequestPage = lazy(() => import("./pages/TransitionRequestPage"));

// Money pages: chunk propio, fuera del bundle inicial (P5.8)
const SeoConsultantMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.SeoConsultantMoneyPage })));
const SeoAuditMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.SeoAuditMoneyPage })));
const TechnicalSeoMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.TechnicalSeoMoneyPage })));
const InternationalSeoMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.InternationalSeoMoneyPage })));
const HispanicSeoMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.HispanicSeoMoneyPage })));
const EcommerceSeoMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.EcommerceSeoMoneyPage })));
const AmazonSeoMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.AmazonSeoMoneyPage })));
const AiSearchMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.AiSearchMoneyPage })));
const GoogleAdsMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.GoogleAdsMoneyPage })));
const SeoSemStrategiesMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.SeoSemStrategiesMoneyPage })));
const RebrandingSeoMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.RebrandingSeoMoneyPage })));
const BrandingMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.BrandingMoneyPage })));
const WebsiteRedesignMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.WebsiteRedesignMoneyPage })));
const ContentConversionMoneyPage = lazy(() => import("./pages/money/MoneyPages").then((m) => ({ default: m.ContentConversionMoneyPage })));


const LibraryHubPage = lazy(() => import("./pages/library/LibraryHubPage"));
import { RouteRobotsMeta } from "./components/RouteRobotsMeta";
import { SiteHead } from "./components/seo/SiteHead";
// Search Library articles: chunk propio (P5.8)
const AuditVsStrategyArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.AuditVsStrategyArticle })));
const TechnicalPrioritizationArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.TechnicalPrioritizationArticle })));
const SeoForAiSearchArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.SeoForAiSearchArticle })));
const GeoAeoAiSeoArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.GeoAeoAiSeoArticle })));
const KeywordsNotInternationalArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.KeywordsNotInternationalArticle })));
const HispanicMarketSeoArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.HispanicMarketSeoArticle })));
const CategoryVsProductArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.CategoryVsProductArticle })));
const AmazonSeoVsAdsArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.AmazonSeoVsAdsArticle })));
const ConsultantVsAgencyArticle = lazy(() => import("./pages/library/LibraryArticles").then((m) => ({ default: m.ConsultantVsAgencyArticle })));

const queryClient = new QueryClient();

function ScrollToTopWrapper() {
  useScrollToTop();
  usePageViewTracking();
  return null;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTopWrapper />
            <SiteHead />
            <Suspense fallback={null}>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<AuthPage />} />
            {/* Protected Pages */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
              
            {/* Main Pages - ES */}
            <Route path="/sobre-mi" element={<AboutPage />} />
            <Route path="/servicios" element={<ServicesPage />} />
            <Route path="/casos-de-exito" element={<CaseStudiesHubPage />} />
            <Route path="/casos-de-exito/:slug" element={<CaseStudyDetailPage />} />
            <Route path="/metodologia" element={<MethodologyPage />} />
            <Route path="/diagnostico-seo-gratis" element={<DiagnosticoSeoPage />} />
            <Route path="/herramientas-seo" element={<SEOToolsPage />} />
            <Route path="/agendar-consultoria" element={<BookingPage />} />
            <Route path="/preguntas-frecuentes" element={<FAQPage />} />
            <Route path="/contacto" element={<ContactPage />} />
            <Route path="/transicion-agencia" element={<TransitionRequestPage />} />
            
            {/* Main Pages - EN (with /en/ prefix) */}
            <Route path="/en" element={<Index />} />
            <Route path="/en/about" element={<AboutPage />} />
            <Route path="/en/services" element={<ServicesPage />} />
            <Route path="/en/case-studies" element={<CaseStudiesHubPage />} />
            <Route path="/en/case-studies/:slug" element={<CaseStudyDetailPage />} />
            <Route path="/en/methodology" element={<MethodologyPage />} />
            <Route path="/en/free-seo-diagnosis" element={<DiagnosticoSeoPage />} />
            <Route path="/en/seo-tools" element={<SEOToolsPage />} />
            <Route path="/en/book-consultation" element={<BookingPage />} />
            <Route path="/en/faq" element={<FAQPage />} />
            <Route path="/en/contact" element={<ContactPage />} />
            <Route path="/en/transition-request" element={<TransitionRequestPage />} />
              
            {/* Legal Pages - ES */}
            <Route path="/terminos-de-servicio" element={<TermsOfServicePage />} />
            <Route path="/politica-de-privacidad" element={<PrivacyPolicyPage />} />
            <Route path="/politica-de-cookies" element={<CookiesPolicyPage />} />
            <Route path="/aviso-legal" element={<DisclaimerPage />} />
            <Route path="/informacion-oficial" element={<OfficialInformationPage />} />
            <Route path="/en/official-information" element={<OfficialInformationPage />} />
            
            {/* Legal Pages - EN (with /en/ prefix) */}
            <Route path="/en/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/en/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/en/cookies-policy" element={<CookiesPolicyPage />} />
            <Route path="/en/disclaimer" element={<DisclaimerPage />} />
            
            {/* Service Pages - ES */}
            <Route path="/consultor-seo" element={<SeoConsultantMoneyPage />} />
            <Route path="/servicios/seo-diagnostico" element={<SeoAuditMoneyPage />} />
            <Route path="/servicios/seo-internacional" element={<InternationalSeoMoneyPage />} />
            <Route path="/servicios/seo-bilingue-mercado-hispano" element={<HispanicSeoMoneyPage />} />
            <Route path="/servicios/ecommerce-seo" element={<EcommerceSeoMoneyPage />} />
            <Route path="/servicios/seo-sem-estrategias" element={<SeoSemStrategiesMoneyPage />} />
            <Route path="/servicios/google-ads" element={<GoogleAdsMoneyPage />} />
            <Route path="/servicios/amazon-consulting" element={<AmazonSeoMoneyPage />} />
            <Route path="/servicios/rebranding-seo" element={<RebrandingSeoMoneyPage />} />
            <Route path="/servicios/branding" element={<BrandingMoneyPage />} />
            <Route path="/servicios/rediseno-web" element={<WebsiteRedesignMoneyPage />} />
            <Route path="/servicios/contenido-conversion" element={<ContentConversionMoneyPage />} />
            <Route path="/servicios/posicionamiento-llm" element={<AiSearchMoneyPage />} />
            <Route path="/servicios/seo-tecnico" element={<TechnicalSeoMoneyPage />} />
            
            {/* Service Pages - EN (with /en/ prefix) */}
            <Route path="/en/seo-consultant" element={<SeoConsultantMoneyPage />} />
            <Route path="/en/services/seo-audit" element={<SeoAuditMoneyPage />} />
            <Route path="/en/services/international-seo" element={<InternationalSeoMoneyPage />} />
            <Route path="/en/services/hispanic-market-seo" element={<HispanicSeoMoneyPage />} />
            <Route path="/en/services/ecommerce-seo" element={<EcommerceSeoMoneyPage />} />
            <Route path="/en/services/seo-sem-strategies" element={<SeoSemStrategiesMoneyPage />} />
            <Route path="/en/services/google-ads" element={<GoogleAdsMoneyPage />} />
            <Route path="/en/services/amazon-consulting" element={<AmazonSeoMoneyPage />} />
            <Route path="/en/services/rebranding-seo" element={<RebrandingSeoMoneyPage />} />
            <Route path="/en/services/branding" element={<BrandingMoneyPage />} />
            <Route path="/en/services/website-redesign" element={<WebsiteRedesignMoneyPage />} />
            <Route path="/en/services/content-conversion" element={<ContentConversionMoneyPage />} />

            {/* Search Library (P3) */}
            <Route path="/biblioteca" element={<LibraryHubPage />} />
            <Route path="/en/library" element={<LibraryHubPage />} />
            <Route path="/biblioteca/auditoria-seo-o-estrategia-seo" element={<AuditVsStrategyArticle />} />
            <Route path="/en/library/seo-audit-vs-seo-strategy" element={<AuditVsStrategyArticle />} />
            <Route path="/biblioteca/priorizacion-seo-tecnico" element={<TechnicalPrioritizationArticle />} />
            <Route path="/en/library/technical-seo-prioritization" element={<TechnicalPrioritizationArticle />} />
            <Route path="/biblioteca/seo-para-ai-search" element={<SeoForAiSearchArticle />} />
            <Route path="/en/library/seo-for-ai-search" element={<SeoForAiSearchArticle />} />
            <Route path="/biblioteca/geo-aeo-y-ai-seo" element={<GeoAeoAiSeoArticle />} />
            <Route path="/en/library/geo-aeo-and-ai-seo" element={<GeoAeoAiSeoArticle />} />
            <Route path="/biblioteca/traducir-keywords-no-es-seo-internacional" element={<KeywordsNotInternationalArticle />} />
            <Route path="/en/library/translating-keywords-is-not-international-seo" element={<KeywordsNotInternationalArticle />} />
            <Route path="/biblioteca/seo-mercado-hispano-estados-unidos" element={<HispanicMarketSeoArticle />} />
            <Route path="/en/library/seo-us-hispanic-market" element={<HispanicMarketSeoArticle />} />
            <Route path="/biblioteca/ecommerce-categorias-vs-productos" element={<CategoryVsProductArticle />} />
            <Route path="/en/library/ecommerce-category-vs-product-pages" element={<CategoryVsProductArticle />} />
            <Route path="/biblioteca/amazon-seo-vs-amazon-ads" element={<AmazonSeoVsAdsArticle />} />
            <Route path="/en/library/amazon-seo-vs-amazon-advertising" element={<AmazonSeoVsAdsArticle />} />
            <Route path="/biblioteca/consultor-seo-o-agencia-seo" element={<ConsultantVsAgencyArticle />} />
            <Route path="/en/library/seo-consultant-vs-seo-agency" element={<ConsultantVsAgencyArticle />} />
            <Route path="/en/services/llm-positioning" element={<AiSearchMoneyPage />} />
            <Route path="/en/services/technical-seo" element={<TechnicalSeoMoneyPage />} />
            
            {/* Google Ads Landing Pages (high-intent keywords) */}
            <Route path="/publicidad-google-ads-houston" element={<GoogleAdsPage />} />
            <Route path="/google-ads-advertising-houston" element={<GoogleAdsPage />} />
            <Route path="/google-ads-abogados-texas" element={<GoogleAdsPage />} />
            <Route path="/google-ads-lawyers-texas" element={<GoogleAdsPage />} />
            
            {/* The Woodlands Pages (bilingual) */}
            <Route path="/seo-the-woodlands" element={<SEOTheWoodlandsPage />} />
            <Route path="/google-ads-the-woodlands" element={<GoogleAdsTheWoodlandsPage />} />
            <Route path="/branding-web-the-woodlands" element={<BrandingWebTheWoodlandsPage />} />
            <Route path="/ecommerce-marketplaces-the-woodlands" element={<EcommerceMarketplacesTheWoodlandsPage />} />
            <Route path="/social-media-marketing-the-woodlands" element={<SocialMediaTheWoodlandsPage />} />
            
            {/* Local SEO Hub - ES */}
            <Route path="/seo-local" element={<LocalSEOHubPage />} />
            <Route path="/seo-local-houston" element={<LocalSEOHoustonPage />} />
            
            {/* Local SEO Hub - EN (with /en/ prefix) */}
            <Route path="/en/local-seo" element={<LocalSEOHubPage />} />
            <Route path="/en/local-seo-houston" element={<LocalSEOHoustonPage />} />
            
            {/* Location Hubs - ES */}
            <Route path="/servicios-locales-houston" element={<LocationsHubPage />} />
            <Route path="/servicios-locales-florida" element={<LocationsFloridaHubPage />} />
            
            {/* Location Hubs - EN (with /en/ prefix) */}
            <Route path="/en/local-services-houston" element={<LocationsHubPage />} />
            <Route path="/en/local-services-florida" element={<LocationsFloridaHubPage />} />
            
            {/* Texas Location Pages (bilingual - same content) */}
            <Route path="/seo-houston" element={<SEOHoustonPage />} />
            <Route path="/google-ads-houston" element={<GoogleAdsHoustonPage />} />
            <Route path="/seo-spring" element={<SEOSpringPage />} />
            <Route path="/google-ads-spring" element={<GoogleAdsSpringPage />} />
            <Route path="/seo-conroe" element={<SEOConroePage />} />
            <Route path="/google-ads-conroe" element={<GoogleAdsConroePage />} />
            <Route path="/seo-katy" element={<SEOKatyPage />} />
            <Route path="/google-ads-katy" element={<GoogleAdsKatyPage />} />
            <Route path="/seo-sugar-land" element={<SEOSugarLandPage />} />
            <Route path="/google-ads-sugar-land" element={<GoogleAdsSugarLandPage />} />
            <Route path="/seo-tomball" element={<SEOTomballPage />} />
            <Route path="/google-ads-tomball" element={<GoogleAdsTomballPage />} />
            <Route path="/seo-magnolia" element={<SEOMagnoliaPage />} />
            <Route path="/google-ads-magnolia" element={<GoogleAdsMagnoliaPage />} />
            <Route path="/seo-memorial" element={<SEOMemorialPage />} />
            <Route path="/google-ads-memorial" element={<GoogleAdsMemorialPage />} />
            
            {/* Florida Location Pages */}
            <Route path="/seo-orlando" element={<SEOOrlandoPage />} />
            <Route path="/google-ads-orlando" element={<GoogleAdsOrlandoPage />} />
            <Route path="/seo-miami" element={<SEOMiamiPage />} />
            <Route path="/google-ads-miami" element={<GoogleAdsMiamiPage />} />
            
            {/* SEO Tools - ES */}
            <Route path="/herramientas/contador-palabras" element={<WordCounterPage />} />
            <Route path="/herramientas/generador-sitemap" element={<SitemapGeneratorPage />} />
            <Route path="/herramientas/corrector-ortografico" element={<SpellCheckerPage />} />
            <Route path="/herramientas/verificador-blacklist" element={<BlacklistCheckerPage />} />
            <Route path="/herramientas/analizador-seo-local-gmb" element={<LocalSEOGMBAnalyzerPage />} />
            <Route path="/herramientas/analizador-penalizaciones-google" element={<GooglePenalizationAnalyzerPage />} />
            <Route path="/herramientas/generador-metatags" element={<MetaTagsGeneratorPage />} />
            <Route path="/herramientas/analizador-url" element={<URLAnalyzerPage />} />
            <Route path="/herramientas/densidad-palabras" element={<KeywordDensityPage />} />
            <Route path="/herramientas/generador-contenido" element={<AIContentGeneratorPage />} />
            <Route path="/herramientas/chatbot-personalizado" element={<CustomChatbotPage />} />
            <Route path="/herramientas/generador-imagenes" element={<AIImageGeneratorPage />} />
            <Route path="/herramientas/asistente-codigo" element={<CodeAssistantPage />} />
            <Route path="/herramientas/analizador-sentimientos" element={<SentimentAnalyzerPage />} />
            <Route path="/herramientas/seo-mega-tool" element={<SEOMegaToolPage />} />
            
            {/* SEO Tools - EN (with /en/ prefix) */}
            <Route path="/en/tools/word-counter" element={<WordCounterPage />} />
            <Route path="/en/tools/sitemap-generator" element={<SitemapGeneratorPage />} />
            <Route path="/en/tools/spell-checker" element={<SpellCheckerPage />} />
            <Route path="/en/tools/blacklist-checker" element={<BlacklistCheckerPage />} />
            <Route path="/en/tools/local-seo-gmb-analyzer" element={<LocalSEOGMBAnalyzerPage />} />
            <Route path="/en/tools/google-penalization-analyzer" element={<GooglePenalizationAnalyzerPage />} />
            <Route path="/en/tools/meta-tags-generator" element={<MetaTagsGeneratorPage />} />
            <Route path="/en/tools/url-analyzer" element={<URLAnalyzerPage />} />
            <Route path="/en/tools/keyword-density" element={<KeywordDensityPage />} />
            <Route path="/en/tools/ai-content-generator" element={<AIContentGeneratorPage />} />
            <Route path="/en/tools/custom-chatbot" element={<CustomChatbotPage />} />
            <Route path="/en/tools/ai-image-generator" element={<AIImageGeneratorPage />} />
            <Route path="/en/tools/code-assistant" element={<CodeAssistantPage />} />
            <Route path="/en/tools/sentiment-analyzer" element={<SentimentAnalyzerPage />} />
            <Route path="/en/tools/seo-mega-tool" element={<SEOMegaToolPage />} />
            
            {/* Blog Routes */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/en/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/en/blog/:slug" element={<BlogPostPage />} />
            <Route path="/blog/implementar-ia-marketing-digital" element={<ImplementarIAMarketing />} />
            <Route path="/en/blog/implement-ai-digital-marketing-strategy" element={<ImplementarIAMarketing />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="/visual-lab/roc-v2" element={<RocV2Page />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
            </Suspense>
          <RouteRobotsMeta />

        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
</HelmetProvider>
);

export default App;
